/// <reference types="vite/client" />
import { Buffer } from 'buffer'
import pdfWorkerSrc from 'pdfjs-dist/build/pdf.worker?url'

export const PASSWORD_PROTECTED_TOASTER_MSG =
  'This file is password protected. Please remove the password and try again'
export const MICROSITE_PASSWORD_HINT =
  'Password-protected file not supported'
export const FILE_READ_ERROR_TOASTER_MSG =
  'Invalid or corrupted file. Please try another file.'

const PASSWORD_CHECK_EXTENSIONS = new Set([
  'pdf',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'ppt',
  'pptx',
  'docm',
  'xlsm',
])

export type PasswordDetectResult = 'ok' | 'blocked_password' | 'error'
export type PasswordProtectionPlatform = 'microsite' | 'neoengage'

let pdfWorkerInitialized = false

export function getExtensionLower(fileName: string): string {
  const i = fileName.lastIndexOf('.')
  if (i < 0) return ''
  return fileName.slice(i + 1).toLowerCase()
}

function isPasswordCheckExtension(ext: string): boolean {
  return PASSWORD_CHECK_EXTENSIONS.has(ext)
}

function shouldCheckPasswordForFile(file: File): boolean {
  return isPasswordCheckExtension(getExtensionLower(file.name))
}

function ensureBufferPolyfill() {
  if (
    typeof globalThis !== 'undefined' &&
    !(globalThis as unknown as { Buffer?: unknown }).Buffer
  ) {
    ;(globalThis as unknown as { Buffer: typeof Buffer }).Buffer = Buffer
  }
}

export async function detectPasswordProtectedFile(
  file: File
): Promise<PasswordDetectResult> {
  if (!shouldCheckPasswordForFile(file)) {
    return 'ok'
  }
  const ext = getExtensionLower(file.name)
  const ab = await file.arrayBuffer()
  if (ext === 'pdf') {
    return detectPdfPasswordProtectedInternal(ab)
  }
  return detectOfficeEncryptedInternal(ab)
}

async function detectPdfPasswordProtectedInternal(
  data: ArrayBuffer
): Promise<PasswordDetectResult> {
  const pdfjs = await import('pdfjs-dist')
  if (!pdfWorkerInitialized) {
    pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerSrc
    pdfWorkerInitialized = true
  }
  const { getDocument, InvalidPDFException } = pdfjs
  const uint8 = new Uint8Array(data)
  const loadingTask = getDocument({ data: uint8, stopAtErrors: true })
  try {
    const doc = await loadingTask.promise
    if (doc) {
      await doc.destroy()
    }
    return 'ok'
  } catch (e: unknown) {
    void loadingTask.destroy().catch(() => undefined)
    const name = (e as { name?: string })?.name
    if (name === 'PasswordException') {
      return 'blocked_password'
    }
    if (e instanceof InvalidPDFException || name === 'InvalidPDFException') {
      return 'error'
    }
    return 'error'
  }
}

async function detectOfficeEncryptedInternal(
  ab: ArrayBuffer
): Promise<PasswordDetectResult> {
  try {
    ensureBufferPolyfill()
    const mod = (await import('officecrypto-tool')) as {
      default?: { isEncrypted: (input: Buffer) => boolean }
      isEncrypted?: (input: Buffer) => boolean
    }
    const isEncFn = mod.isEncrypted ?? mod.default?.isEncrypted
    if (!isEncFn) {
      return 'error'
    }
    const buf = Buffer.from(new Uint8Array(ab))
    return isEncFn(buf) ? 'blocked_password' : 'ok'
  } catch {
    return 'error'
  }
}

export async function filterFilesWithPasswordCheck(
  fileList: File[],
  onResult?: (result: PasswordDetectResult, file: File) => void
): Promise<File[]> {
  const safe: File[] = []
  for (const file of fileList) {
    if (!shouldCheckPasswordForFile(file)) {
      safe.push(file)
      continue
    }
    const result = await detectPasswordProtectedFile(file)
    if (result !== 'ok') {
      onResult?.(result, file)
    }
    if (result === 'ok') {
      safe.push(file)
    }
  }
  return safe
}
